/**
 * Integration health log — frontend client.
 *
 * Mirrors the backend's /api/integration-events router. Two surfaces:
 *
 *   - useIntegrationEvents()  — React hook for the "Recent issues"
 *     panel. Polls for new events while the tab is visible.
 *
 *   - reportClientIssue(...)  — fire-and-forget beacon called from
 *     error paths in the email module so client-side failures (fetch
 *     aborted, autocomplete network error, sync rejected) end up in
 *     the same timeline as server-side ones.
 *
 * Beacon failures are silent on purpose: the recorder must never
 * cascade into the user's actual flow. The console keeps a compact
 * trace so devs/support can see what was attempted in the open tab.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import api, { getAccessToken } from '../hooks/useApi'

/**
 * Session-scoped persistence for the two "stop probing" latches below
 * (endpoint-missing 404, and auth-rejected 401).
 *
 * WHY: both latches were module-scope only, which is wiped on every full
 * page reload AND on hot-module-replace. So each reload of e.g.
 * email.quotech.ai (whose backend 401s this endpoint) re-armed the cold
 * probes and the operator saw a fresh pair of `GET /api/integration-events
 * … 401` console errors on EVERY load (operator 2026-06-20). sessionStorage
 * is per-origin (so each tenant subdomain keeps its own decision) and
 * survives reload + HMR within the tab session, so once we've learned "this
 * backend rejects/omits the endpoint" we never re-probe until the tab is
 * closed (or a manual force-refresh succeeds and clears the latch).
 *
 * Keys are NOT tenant-slugged on purpose — sessionStorage is already
 * origin-isolated, and a literal tenant slug here would trip the
 * cross-tenant string audit. Failures (private mode / SSR) are swallowed:
 * the in-memory latch still works, we just lose cross-reload memory.
 */
const AUTH_FAILED_SS_KEY = 'qt.integrationEvents.authFailed'
const ENDPOINT_DISABLED_SS_KEY = 'qt.integrationEvents.endpointDisabled'

function readSessionFlag(key: string): boolean {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function writeSessionFlag(key: string, on: boolean): void {
  try {
    if (typeof sessionStorage === 'undefined') return
    if (on) sessionStorage.setItem(key, '1')
    else sessionStorage.removeItem(key)
  } catch {
    // private mode / SSR / quota — fall back to the in-memory latch only
  }
}

/**
 * Some Quotech backends do not expose `/api/integration-events` (the
 * dashboard.quotech.ai Django service is one — the endpoint lives on
 * the SAM/Newmark FastAPI service). When we detect a 404, we cache
 * that fact at module scope and short-circuit subsequent calls so:
 *
 *   1. The console isn't spammed with 404s every 30/60 seconds.
 *   2. The backend isn't hit with futile polling traffic.
 *   3. The "Recent issues" panel renders an empty (not error) state.
 *
 * Module-level (not React state) so all hook instances share the
 * decision after the first probe lands. Hydrated from sessionStorage so
 * it also survives reload/HMR within the tab session (a host shipping the
 * endpoint requires a deploy → new tab/session anyway).
 */
let integrationEventsEndpointDisabled = readSessionFlag(ENDPOINT_DISABLED_SS_KEY)
let warnedAboutDisabledEndpoint = false

function isAxios404(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { response?: { status?: number }; isAxiosError?: boolean }
  return !!e.isAxiosError && e.response?.status === 404
}

function noteEndpointDisabled(scope: string): void {
  integrationEventsEndpointDisabled = true
  writeSessionFlag(ENDPOINT_DISABLED_SS_KEY, true)
  if (warnedAboutDisabledEndpoint) return
  warnedAboutDisabledEndpoint = true
  try {
    // eslint-disable-next-line no-console
    console.warn(
      `[email-hub] /api/integration-events not available on this backend ` +
        `(scope: ${scope}); integration-events panel + beacon disabled for this session.`,
    )
  } catch {
    // ignore logging failures
  }
}

/**
 * Module-scope auth-failure latch (BUG-0038 follow-up).
 *
 * The original BUG-0038 fix put `authFailed` in per-hook-instance React
 * state, which left two gaps:
 *
 *   1. **Burst-of-fetches on cold mount.** `useEffect(() => refresh(), [])`
 *      fires the initial fetch, AND visibility/focus listeners installed
 *      by the poll effect can immediately fire `refreshRef.current()` BEFORE
 *      the first 401 returns and `setAuthFailed(true)` propagates through
 *      React's render cycle. Each in-flight fetch produces its own 401 →
 *      operator sees 2–4 console errors per mount before the brake engages.
 *
 *   2. **Reset on remount.** Any code path that unmounts + remounts the
 *      hook (panel close+reopen, route navigation that tears down EmailLayout
 *      via a keyed wrapper, hot-module-replace in dev) resets the latch
 *      and we re-fire the initial fetch into another 401.
 *
 * The 404 short-circuit above already uses a module-scope flag because the
 * "endpoint missing" condition survives mount cycles. The 401 condition is
 * the same shape — once we've seen a 401 in this session, every subsequent
 * fetch from the same browser session will also 401 until the underlying
 * auth state changes (which we'll detect via a manual force-refresh
 * returning 200). Hoist to module scope, expose via `useSyncExternalStore`
 * so React renders propagate, and check the latch SYNCHRONOUSLY inside
 * `refresh()` so concurrent in-flight fetches dedup at the call site.
 *
 * Scope is the PAGE, not the tab (change #294): it is cleared by a reload, by
 * a manual force-refresh that returns 200, or by closing the tab. It used to
 * persist across reloads to silence a repeated cold-probe 401 pair (operator
 * 2026-06-20), but that predates the interceptor giving this path
 * refresh-and-replay — with recovery available, a sticky "broken" bit across
 * reloads is how a routine token expiry became a permanent dead panel.
 */
let integrationEventsAuthFailed = false
// Change #294: the latch is deliberately NOT restored from sessionStorage any
// more, and any value an older build left behind is evicted on load. Reason:
// the interceptor now gives this path the same refresh-and-replay every other
// endpoint gets, so a 401 that still survives means the session itself is
// unusable — and a page load is exactly when the gate re-mints one. Persisting
// "broken" across reloads is what made an ordinary token expiry look like a
// permanent account-permission failure for the whole tab.
writeSessionFlag(AUTH_FAILED_SS_KEY, false)
const authFailedListeners = new Set<() => void>()

function setAuthFailedLatch(next: boolean): void {
  if (integrationEventsAuthFailed === next) return
  integrationEventsAuthFailed = next
  for (const listener of authFailedListeners) {
    try {
      listener()
    } catch {
      // ignore subscriber failures — a buggy subscriber must not block
      // the rest of the panel from seeing the latch flip
    }
  }
}

function subscribeAuthFailed(callback: () => void): () => void {
  authFailedListeners.add(callback)
  return () => {
    authFailedListeners.delete(callback)
  }
}

function getAuthFailedSnapshot(): boolean {
  return integrationEventsAuthFailed
}

function getAuthFailedServerSnapshot(): boolean {
  return false
}

function useIntegrationEventsAuthFailed(): boolean {
  return useSyncExternalStore(
    subscribeAuthFailed,
    getAuthFailedSnapshot,
    getAuthFailedServerSnapshot,
  )
}

export type IntegrationEventSeverity = 'info' | 'warning' | 'error'

export interface IntegrationEvent {
  id: number
  source: string
  severity: IntegrationEventSeverity
  reason: string | null
  message: string
  request_id: string | null
  context: Record<string, unknown> | null
  detail: string | null
  user_id: number | null
  created_at: string | null
}

/**
 * Shared event cache across every `useIntegrationEvents` instance.
 * WHY (operator 2026-07-31): layout mounts one hook for the badge count,
 * the drawer mounts a SECOND hook with empty React state. When the
 * auth-failed latch is set, the drawer's refresh() early-returns and
 * never hydrates — badge shows "49" while the panel says "No issues".
 * Module-scope publish (same pattern as the auth latch) keeps badge
 * and panel on one timeline.
 */
let sharedIntegrationEvents: IntegrationEvent[] = []
const sharedEventsListeners = new Set<() => void>()

function publishSharedEvents(next: IntegrationEvent[]): void {
  sharedIntegrationEvents = next
  for (const listener of sharedEventsListeners) {
    try {
      listener()
    } catch {
      /* ignore subscriber failures */
    }
  }
}

function subscribeSharedEvents(callback: () => void): () => void {
  sharedEventsListeners.add(callback)
  return () => {
    sharedEventsListeners.delete(callback)
  }
}

function getSharedEventsSnapshot(): IntegrationEvent[] {
  return sharedIntegrationEvents
}

function getSharedEventsServerSnapshot(): IntegrationEvent[] {
  return []
}

function useSharedIntegrationEvents(): IntegrationEvent[] {
  return useSyncExternalStore(
    subscribeSharedEvents,
    getSharedEventsSnapshot,
    getSharedEventsServerSnapshot,
  )
}

interface ListResponse {
  results: IntegrationEvent[]
  max_id: number | null
  count: number
}

export interface ReportIssueInput {
  source: string
  message: string
  severity?: IntegrationEventSeverity
  reason?: string
  requestId?: string
  context?: Record<string, unknown>
  detail?: string
}

/** Mint a short-ish correlation token for tying client + server rows
 *  together. Not a UUID — we don't need cryptographic uniqueness, just
 *  human-grep-ability across logs. Format: `{prefix}-{base36 time}-{rand}`.
 */
export function newRequestId(prefix = 'fe'): string {
  const t = Date.now().toString(36)
  const r = Math.random().toString(36).slice(2, 6)
  return `${prefix}-${t}-${r}`
}

/** Fire-and-forget beacon. Never throws.
 *
 *  We always console.warn locally too — when support pulls a screen
 *  recording or asks the user for "the console", they'll see the same
 *  failures the server log captured.
 */
export async function reportClientIssue(input: ReportIssueInput): Promise<void> {
  const body = {
    source: input.source,
    message: input.message,
    severity: input.severity ?? 'warning',
    reason: input.reason,
    request_id: input.requestId,
    context: input.context,
    detail: input.detail,
  }

  // Local breadcrumb so the open tab carries the same evidence the
  // server side will. Use the matching console method by severity.
  const consoleFn =
    body.severity === 'error'
      ? console.error
      : body.severity === 'warning'
        ? console.warn
        : console.info
  try {
    consoleFn('[integration-event]', input.source, input.message, {
      reason: input.reason,
      context: input.context,
      requestId: input.requestId,
    })
  } catch {
    // ignore logging failures
  }

  if (integrationEventsEndpointDisabled) {
    // Endpoint already known-missing on this backend; skip the POST.
    return
  }
  // Phase 1 (Email→Quote ARC § 3.1): pre-flight bearer check. The
  // beacon fires from error paths in the email module (auto-sync
  // queued, contacts search failed, compose rejected). On a cold
  // mount where the ApiAuthGate hasn't yet bound the bearer (or
  // after a session-level wipe), the POST is guaranteed to 401.
  // Skip the wire entirely to keep the console clean — we still
  // console.warn'd locally above so support has the same evidence
  // a successful beacon would have surfaced.
  if (!getAccessToken()) {
    return
  }
  // BUG-2026-06-27-INTEGRATION-EVENTS-BEACON-401-NO-LATCH: if the GET poll
  // (or a prior beacon) already saw a 401 on this endpoint this session, the
  // bearer this origin holds can't use /integration-events — re-POSTing only
  // prints another `POST … 401` in the console on EVERY email action
  // (operator on dashboard.quotech.ai, where the endpoint 401s). Honour the
  // same module-scope auth-failed latch the GET poll already checks + arms.
  if (integrationEventsAuthFailed) {
    return
  }
  try {
    await api.post('/integration-events', body)
  } catch (err) {
    if (isAxios404(err)) {
      noteEndpointDisabled('reportClientIssue')
      return
    }
    // BUG-0038: a 401 on the beacon must NOT escalate — the response
    // interceptor in useApi already carves /integration-events out of
    // the session-teardown path, but we also stop logging "beacon
    // failed" because it'll fire on every error path until the
    // session is repaired and is pure noise to the operator.
    const status = (err as { response?: { status?: number } })?.response?.status
    if (status === 401) {
      // BUG-2026-06-27: arm the shared latch (symmetric with the GET poll)
      // so subsequent beacons skip the wire for the rest of the session —
      // the operator sees ONE `POST … 401`, not one per email action. A 200
      // force-refresh (or a new tab) clears it.
      setAuthFailedLatch(true)
      return
    }
    // Beacon failures are non-fatal. Don't escalate — the user's
    // primary flow continues. Console-warn so devs see it.
    try {
      console.warn('[integration-event] beacon failed', err)
    } catch {
      // ignore
    }
  }
}

interface UseIntegrationEventsOptions {
  severity?: IntegrationEventSeverity
  source?: string
  sourcePrefix?: string
  /** Poll interval in ms; defaults to 60s. Set to 0 to disable polling. */
  pollMs?: number
  /** Initial page size and refetch limit. Defaults to 50. */
  limit?: number
}

interface UseIntegrationEventsReturn {
  events: IntegrationEvent[]
  loading: boolean
  error: string | null
  /** Trigger a one-shot fetch. By default skips when the auth-failure
   *  latch is set (BUG-0038) so a manual refresh from a still-broken
   *  session doesn't produce another 401 in the console. Pass
   *  `{ force: true }` to ATTEMPT the fetch regardless — the recovery
   *  path used by the "Refresh" button in IntegrationEventsPanel. A
   *  successful (200) force-refresh clears the latch and resumes
   *  live polling across every mounted hook instance. */
  refresh: (opts?: { force?: boolean }) => Promise<void>
  /** Number of events with severity='error' currently held in state. */
  errorCount: number
  /** Number of events with severity='warning' currently held in state. */
  warningCount: number
  /** Bulk delete events for the caller's organisation. Pass an
   *  optional `beforeId` to scope the clear to events <= that id. */
  clear: (beforeId?: number) => Promise<number>
  /** Whether a clear request is currently in flight. */
  clearing: boolean
  /** BUG-0038: the most recent fetch returned 401 — auto-polling is
   *  suspended and the panel should render an "unavailable for this
   *  session" affordance instead of a stale list. A manual `refresh()`
   *  is still wired through (in case the session was repaired
   *  out-of-band). Resets to false on the next 200 response. */
  authFailed: boolean
}

/** Hook for the "Recent issues" panel.
 *
 *  Poll cadence is visibility-aware: while the tab is hidden we stop
 *  the timer; when it becomes visible again we refetch immediately
 *  *and* restart the timer. This avoids burning quota for nobody and
 *  guarantees the panel is fresh the moment the user returns.
 */
export function useIntegrationEvents(
  opts: UseIntegrationEventsOptions = {},
): UseIntegrationEventsReturn {
  const { severity, source, sourcePrefix, pollMs = 60_000, limit = 50 } = opts
  // Shared module cache — not per-instance React state (see publishSharedEvents).
  const allEvents = useSharedIntegrationEvents()
  const events = severity
    ? allEvents.filter((e) => e.severity === severity)
    : allEvents
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // BUG-0038: subscribe to the module-scope auth-failure latch so this
  // hook re-renders (and its effects re-evaluate) whenever ANY hook
  // instance flips the latch. See the rationale block on
  // `integrationEventsAuthFailed` above — module scope is what makes
  // remounts (panel reopen, route nav) NOT re-fire the initial fetch.
  const authFailed = useIntegrationEventsAuthFailed()

  const refresh = useCallback(async (opts?: { force?: boolean }) => {
    if (integrationEventsEndpointDisabled) {
      // Endpoint already known-missing on this backend; render an
      // empty (not error) state so the panel/footer badge collapse
      // gracefully instead of flashing a 404 banner forever.
      publishSharedEvents([])
      setError(null)
      setLoading(false)
      return
    }
    // BUG-0038: synchronous module-latch check. The poll effect already
    // bails on `authFailed` via React state, but visibility/focus
    // listeners that fire between mount and the first 401's render
    // cycle can call refreshRef.current() multiple times before
    // setAuthFailed propagates. Reading the module-scope flag here
    // catches every one of those concurrent calls, so the operator
    // sees ONE 401 per session-state transition instead of 2–4. The
    // `force` opt skips the latch — used by the manual "Refresh"
    // button so the operator can attempt recovery after a repair.
    // Early-return keeps the SHARED last-good cache so a second hook
    // (the drawer) still paints the same rows the badge counted.
    if (!opts?.force && integrationEventsAuthFailed) {
      setLoading(false)
      return
    }
    // Phase 1 (Email→Quote ARC § 3.1): pre-flight bearer check. On
    // cold mount the hook's initial useEffect fires before
    // ApiAuthGate has called setAccessToken(), so the in-flight
    // GET hits /integration-events without an Authorization header
    // and the backend returns 401. The auth-tolerant interceptor in
    // useApi.ts swallows the side-effects (token wipe + redirect)
    // but the raw 401 still surfaces in DevTools as console noise.
    // Skipping when the bearer isn't yet bound resolves cold-mount
    // noise; the visibility-poll interval will pick the panel up
    // once auth lands (no `force` needed because the latch wasn't
    // set — this branch is a no-op short-circuit, not a sticky
    // disablement).
    if (!opts?.force && !getAccessToken()) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      // Always fetch the unfiltered timeline into the shared cache.
      // Severity tabs in the drawer filter client-side so a drawer
      // open on "Error" cannot wipe the badge's full count.
      const params: Record<string, string> = {
        limit: String(Math.max(limit, 100)),
      }
      if (source) params.source = source
      if (sourcePrefix) params.source_prefix = sourcePrefix
      const { data } = await api.get<ListResponse>('/integration-events', { params })
      publishSharedEvents(Array.isArray(data?.results) ? data.results : [])
      setAuthFailedLatch(false)
    } catch (err) {
      if (isAxios404(err)) {
        noteEndpointDisabled('useIntegrationEvents')
        publishSharedEvents([])
        setError(null)
        return
      }
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 401) {
        // Suspend auto-polling for EVERY hook instance — the response
        // interceptor in useApi already swallows the session-teardown
        // side-effects for this path (BUG-0038 carve-out), the panel
        // UI will render a friendly "unavailable for this session"
        // message off the latch, and a manual `refresh({ force: true })`
        // is the recovery path.
        setAuthFailedLatch(true)
      }
      const msg = err instanceof Error ? err.message : 'Failed to load events'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [source, sourcePrefix, limit])

  // Initial fetch — gated on the module latch so a remount after a
  // session-level 401 doesn't re-fire the initial probe (and re-spam
  // the console with another 401). When the latch resets via a
  // successful manual refresh, useSyncExternalStore re-renders this
  // hook, `authFailed` flips to false, the effect re-runs and the
  // hook resumes live updates without a page reload.
  useEffect(() => {
    if (authFailed) return
    void refresh()
  }, [refresh, authFailed])

  // Visibility-aware polling. Mirrors useVisiblePolling in useEmails so
  // both surfaces feel "live" while the tab is open. Suspended when
  // `authFailed` is latched (BUG-0038) — the effect re-runs, returns
  // early, the cleanup tears down any in-flight interval + listeners.
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh
  useEffect(() => {
    if (!pollMs || authFailed) return
    let intervalId: number | null = null
    const start = () => {
      if (intervalId !== null) return
      // If the backend returned 404 since this hook mounted, stop
      // scheduling new polls — refresh() is a no-op anyway, but
      // skipping the interval keeps the JS event loop quieter.
      if (integrationEventsEndpointDisabled) return
      intervalId = window.setInterval(() => {
        if (integrationEventsEndpointDisabled) {
          window.clearInterval(intervalId!)
          intervalId = null
          return
        }
        if (!document.hidden) void refreshRef.current()
      }, pollMs)
    }
    const stop = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId)
        intervalId = null
      }
    }
    const onVisibility = () => {
      if (document.hidden) {
        stop()
      } else {
        void refreshRef.current()
        start()
      }
    }
    if (!document.hidden) start()
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onVisibility)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onVisibility)
    }
  }, [pollMs, authFailed])

  // Badge counts always come from the unfiltered shared cache so a
  // drawer severity tab cannot shrink the dock badge.
  const errorCount = allEvents.filter(e => e.severity === 'error').length
  const warningCount = allEvents.filter(e => e.severity === 'warning').length

  const [clearing, setClearing] = useState(false)
  const clear = useCallback(async (beforeId?: number): Promise<number> => {
    if (integrationEventsEndpointDisabled) {
      // Nothing to clear — the endpoint isn't there. Treat as success
      // so the UI moves out of the clearing state cleanly.
      publishSharedEvents([])
      return 0
    }
    setClearing(true)
    try {
      const params: Record<string, string> = {}
      if (beforeId !== undefined) params.before_id = String(beforeId)
      const { data } = await api.delete<{ deleted: number }>('/integration-events', { params })
      // Optimistically drop cleared rows from shared cache — the next
      // poll cycle will reconcile if anything new arrived in between.
      if (beforeId !== undefined) {
        publishSharedEvents(sharedIntegrationEvents.filter(e => e.id > beforeId))
      } else {
        publishSharedEvents([])
      }
      return data?.deleted ?? 0
    } finally {
      setClearing(false)
    }
  }, [])

  return { events, loading, error, refresh, errorCount, warningCount, clear, clearing, authFailed }
}
